import type { ReactNode } from "react";
import { Crown, Pencil, Shield, Eye } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserPresence } from "@/hooks/useSessionPresence";
import { SessionStep } from "@/hooks/useSessionSteps";

interface CollaborationBarProps {
  users: UserPresence[];
  steps: SessionStep[];
  currentSubphase: string;
}

const roleConfig: Record<string, { icon: ReactNode; label: string }> = {
  owner: { icon: <Crown className="h-3 w-3 text-amber-500" />, label: "Owner" },
  contributor: { icon: <Pencil className="h-3 w-3 text-primary" />, label: "Contributor" },
  reviewer: { icon: <Shield className="h-3 w-3 text-emerald-500" />, label: "Reviewer" },
  viewer: { icon: <Eye className="h-3 w-3 text-muted-foreground" />, label: "Viewer" },
};

const getInitials = (user: UserPresence) => {
  if (user.name) {
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }
  return user.email?.slice(0, 2).toUpperCase() || '??';
};

const getDisplayName = (user: UserPresence) => {
  return user.name || user.email?.split('@')[0] || 'Unbekannt';
};

export const CollaborationBar = ({ users, steps, currentSubphase }: CollaborationBarProps) => {
  if (users.length === 0) return null;

  // Count contributions per user in the overall session
  const contributionCounts = new Map<string, number>();
  steps.forEach(step => {
    if (step.user_answer) {
      contributionCounts.set(step.created_by, (contributionCounts.get(step.created_by) || 0) + 1);
    }
  });

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-border/50 bg-card/50">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1">
          Live-Teilnehmer
        </p>
        {users.map((user) => {
          const count = contributionCounts.get(user.user_id) || 0;
          
          return (
            <div
              key={user.user_id}
              className="flex items-center gap-2 py-1"
            >
              <div className="relative">
                <Avatar className="h-7 w-7 border border-border">
                  {user.avatar_url && (
                    <AvatarImage src={user.avatar_url} alt={getDisplayName(user)} />
                  )}
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                    {getInitials(user)}
                  </AvatarFallback>
                </Avatar>
                {/* Online indicator */}
                <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-card" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium text-foreground truncate">
                    {getDisplayName(user)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {user.current_subphase && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                      Phase {user.current_subphase}
                    </Badge>
                  )}
                  {user.is_typing && (
                    <span className="text-[10px] text-primary animate-pulse font-medium">
                      tippt…
                    </span>
                  )}
                </div>
              </div>

              {count > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 gap-0.5">
                      <Pencil className="h-2.5 w-2.5" />
                      {count}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{count} {count === 1 ? 'Beitrag' : 'Beiträge'} in dieser Session</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
